import { useEffect, useRef, useState } from 'react';
import type { ColumnKey, ResponseItem } from '../types';
import { ResponseCard } from './ResponseCard';

type ColDef = {
  key: ColumnKey;
  title: string;
  accent: string;      // border top color
  headerColor: string; // header text color
  field: keyof Pick<ResponseItem, 'perfect' | 'wrong' | 'timeSink' | 'ask'>;
  emoji: string;
};

const COLUMNS: ColDef[] = [
  { key: 'perfect',   title: 'Perfect',    accent: 'border-semantic-green', headerColor: 'text-semantic-green', field: 'perfect',  emoji: '✔' },
  { key: 'wrong',     title: 'Wrong',      accent: 'border-semantic-red',   headerColor: 'text-semantic-red',   field: 'wrong',    emoji: '✖' },
  { key: 'time_sink', title: 'Time Sinks', accent: 'border-semantic-amber', headerColor: 'text-semantic-amber', field: 'timeSink', emoji: '◷' },
  { key: 'ask',       title: 'Asks',       accent: 'border-coast-blue',     headerColor: 'text-coast-blue',     field: 'ask',      emoji: '↑' },
];

type Props = {
  items: ResponseItem[];
  currentUser: string;
};

export function KanbanBoard({ items, currentUser }: Props) {
  const [activeIdx, setActiveIdx] = useState(0);
  const stripRef = useRef<HTMLDivElement | null>(null);

  // Update active dot as user swipes columns
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const handler = () => {
      const w = strip.clientWidth;
      const idx = Math.round(strip.scrollLeft / (w * 0.88));
      setActiveIdx(Math.min(COLUMNS.length - 1, Math.max(0, idx)));
    };
    strip.addEventListener('scroll', handler, { passive: true });
    return () => strip.removeEventListener('scroll', handler);
  }, []);

  const jumpTo = (i: number) => {
    const strip = stripRef.current;
    if (!strip) return;
    const w = strip.clientWidth;
    strip.scrollTo({ left: Math.round(i * (w * 0.88 + 12)), behavior: 'smooth' });
  };

  return (
    <div>
      {/* Segmented control jumps between columns on phone */}
      <div className="lg:hidden mb-2 flex justify-center">
        <div className="segmented">
          {COLUMNS.map((c, i) => (
            <button
              key={c.key}
              aria-current={activeIdx === i}
              onClick={() => jumpTo(i)}
            >
              {c.title}
            </button>
          ))}
        </div>
      </div>

      <div ref={stripRef} className="col-strip">
        {COLUMNS.map(col => {
          const cardsHere = items.filter(r => (r[col.field] as string).trim().length > 0);
          return (
            <section key={col.key} className={`border-t-4 ${col.accent} bg-coast-ink flex flex-col`}>
              <header
                className={`col-header ${col.accent} ${col.headerColor} bg-coast-black
                            flex items-center justify-between`}
              >
                <span>{col.emoji}&nbsp;&nbsp;{col.title}</span>
                <span className="text-xs text-coast-muted tabular-nums">{cardsHere.length}</span>
              </header>
              <div className="p-3 space-y-3 min-h-[240px]">
                {cardsHere.length === 0 && (
                  <div className="text-xs text-coast-muted italic py-8 text-center">
                    Nothing here yet this week.
                  </div>
                )}
                {cardsHere.map(r => (
                  <ResponseCard
                    key={r.id + col.key}
                    response={r}
                    columnKey={col.key}
                    currentUser={currentUser}
                    text={r[col.field] as string}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
